import { FileQuestion, WifiOff } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { DeskGrid } from '../components/desk/DeskGrid'
import {
  BenchModule,
  BookDrawdownModule,
  BookExposureModule,
  BookHeaderModule,
  BookValueModule,
  CadenceModule,
  CapRejectsModule,
  DormancyModule,
  ExitValveModule,
  HoldingsHeatModule,
  InceptionModule,
  KillEntriesModule,
  LiveGateModule,
  MirrorFidelityModule,
  MirrorModule,
  OverlayModule,
  ScorecardHistogramModule,
  TaxModule,
} from '../components/desk/BookModules'
import { layoutRole } from '../components/desk/bookModuleHooks'
import { PicksExcessModule, SectorModule } from '../components/desk/FleetModules'
import { useBooks } from '../hooks/useBooks'
import { useIsMobile } from '../hooks/useMediaQuery'
import { BookLegacyBody } from './BookLegacyBody'
import '../components/Dashboard.css'
import './BookPage.css'

/**
 * Per-book detail page. Desktop: a no-scroll DeskGrid whose module set depends
 * on the book's role (research picking analytics, the validation canary, or
 * live containment); each module opens its drawer. Phone keeps the legacy
 * zones (BookLegacyBody). Renders a clean empty state if the :id doesn't
 * resolve to a known account.
 */
export function BookPage() {
  const { id } = useParams<{ id: string }>()
  const numId = Number(id)
  const { books, byId, isLoading, isError } = useBooks()
  const isMobile = useIsMobile()
  const book = Number.isFinite(numId) ? byId(numId) : undefined

  if (isLoading) {
    return (
      <main className="dashboard-content book-page">
        <div className="loading"><div className="spinner" /></div>
      </main>
    )
  }

  if (isError) {
    return (
      <main className="dashboard-content book-page">
        <div className="empty-state book-page-empty">
          <div className="empty-icon"><WifiOff size={32} aria-hidden="true" /></div>
          <div className="empty-text">books unavailable</div>
          <div className="caption">Could not load the book roster. Retrying…</div>
        </div>
      </main>
    )
  }

  if (!book) {
    return (
      <main className="dashboard-content book-page">
        <div className="empty-state book-page-empty">
          <div className="empty-icon"><FileQuestion size={32} aria-hidden="true" /></div>
          <div className="empty-text">Unknown book</div>
          <div className="caption">No account matches id {id ?? '—'}.</div>
        </div>
      </main>
    )
  }

  if (isMobile) return <BookLegacyBody book={book} />

  const layout = layoutRole(book.role)

  return (
    <main className="dashboard-content desk-page book-page">
      <DeskGrid>
        <BookHeaderModule book={book} />
        {layout === 'research' && <PicksExcessModule books={books} booksPending={false} booksError={false} />}
        {layout === 'validation' && <MirrorFidelityModule book={book} />}
        {layout === 'live' && <LiveGateModule book={book} />}
        <BookDrawdownModule book={book} />
        <BookExposureModule book={book} />
        <ExitValveModule book={book} />
        <TaxModule book={book} />

        <BookValueModule book={book} />
        <HoldingsHeatModule book={book} />

        {layout === 'research' && (
          <>
            <ScorecardHistogramModule book={book} span={2} />
            <SectorModule research={book} booksPending={false} />
            <BenchModule book={book} />
            <CadenceModule book={book} />
            <OverlayModule book={book} />
          </>
        )}
        {layout === 'validation' && (
          <>
            <ScorecardHistogramModule book={book} span={3} />
            <SectorModule research={book} booksPending={false} />
            <MirrorModule book={book} />
            <InceptionModule book={book} />
          </>
        )}
        {layout === 'live' && (
          <>
            <KillEntriesModule book={book} />
            <CapRejectsModule book={book} />
            <DormancyModule book={book} />
          </>
        )}
      </DeskGrid>
    </main>
  )
}
